import { authFetch } from '../utils/api';
import { authFetchRequest } from '../utils/api';
import { DRAWING_URL } from '../constants';

// Round to 2 decimal places. Zoho's Unit_Width / Unit_Height / List_Price
// fields reject values with more than 2 decimals (INVALID_DATA, message
// "invalid data", details: maximum_decimal_place: 2). JS floating-point
// math regularly produces values like 29.999999999999996 from inputs that
// should be 30, so we must round before sending the payload.
const round2 = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
};

// Some doorData fields (e.g. installation.jambType) are stored as
// { value, label } objects from react-select-style controls; others are
// plain strings. Zoho text fields reject objects with INVALID_DATA
// (expected_data_type: text), so coerce to a string.
const toText = (v) => {
  if (v == null) return "";
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return String(v.value ?? v.label ?? "");
  return String(v);
};

function getQuoteProductData(doorData, productId) {
  return {
    Unit_Width: round2(doorData?.frameData?.width_d || doorData?.frameData?.mainRowWidth_d || 0),
    Unit_Height: round2(doorData?.frameData?.height_d || ((doorData?.frameData?.uHeight_d || 0) + (doorData?.frameData?.mainRowHeight_d || 0)) || 0),
    List_Price: round2(doorData?.calc?.total),
    Jamb_Type: toText(doorData?.installation?.jambType),
    Exterior_Finish_Type: toText(doorData?.finish?.exteriorFinishType),
    Eticom_Drawing_URL: `${DRAWING_URL}?id=${productId}`,
  };
}

export async function createCrmProduct({ quoteId, itemRow, doorData, authHeaders }) {
  const quoteRow = await authFetch(`/quotes/${quoteId}`);
  if (!quoteRow?.crm_quote_id) return null;

  const product_payload = {
    Product_Name: `${quoteRow.crm_quote_id}A${itemRow.id}`,
    Product_Code: `${doorData?.frameData?.name}`,
    Unit_Price: doorData?.calc?.total
  };

  const product_output = await authFetchRequest(
    `/api/zoho/create-product`,
    product_payload,
    "POST",
    authHeaders
  );
  const product_id = product_output.data?.[0]?.details?.id;

  // Update crm_product_id locally in ItemQuote
  await authFetchRequest(
    `/local/update`,
    {
      table: "ItemQuote",
      column: "crm_product_id",
      newValue: product_id,
      id: `${itemRow.id}`
    },
    "PUT",
    authHeaders
  );

  // Add product to Zoho CRM quote
  const quoteProductData = getQuoteProductData(doorData, product_id);
  const crmProductPayload = {
    action: "add",
    data: {
      Product_Name: {
        id: product_id,
        name: product_payload.Product_Name,
        Product_Code: product_payload.Product_Code
      },
      ...quoteProductData,
    }
  };

  try {
    await authFetchRequest(
      `/api/zoho/modify-quote-product/${quoteRow.crm_quote_id}/${product_id}`,
      crmProductPayload,
      "POST",
      authHeaders
    );
  } catch (error) {
    console.error("Failed to insert quote product:", error);
  }

  return product_id;
}

export async function updateCrmProduct({ quoteId, itemRow, doorData, crm_product_id, authHeaders }) {
  const quoteRow = await authFetch(`/quotes/${quoteId}`);
  if (!quoteRow?.crm_quote_id) return;

  const product_payload = {
    Product_Name: `${quoteRow.crm_quote_id}A${itemRow.id}`,
    Product_Code: `${doorData?.frameData?.name}`,
    Unit_Price: doorData?.calc?.total
  };

  await authFetchRequest(
    `/api/zoho/update-product/${crm_product_id}`,
    product_payload,
    "PUT",
    authHeaders
  );

  // Update product in Zoho CRM quote.
  // If the product is no longer in the Quote's Quoted_Items list (e.g.
  // deleted in Zoho UI, or the original add failed silently when the
  // product was first created), the backend returns 404. Fall back to
  // an "add" call so the item gets re-linked to the Quote instead of
  // silently disappearing from Zoho.
  const quoteProductData = getQuoteProductData(doorData, crm_product_id);

  const tryModify = async (action, data) => {
    return authFetchRequest(
      `/api/zoho/modify-quote-product/${quoteRow.crm_quote_id}/${crm_product_id}`,
      { action, data },
      "POST",
      authHeaders
    );
  };

  try {
    await tryModify("update", quoteProductData);
  } catch (error) {
    // authFetchRequest throws `new Error('Server error 404: ...')`. Match
    // either a 404 from that helper or any "not found"/"Quoted_Items"
    // substring in the body, since the actual Flask error message is
    // "Product not found in Quoted_Items".
    const msg = error?.message || '';
    const looksLikeNotFound =
      /\b404\b/.test(msg) ||
      /not found/i.test(msg) ||
      /Quoted_Items/i.test(msg);
    if (looksLikeNotFound) {
      // Re-link by adding the existing product to the Quote.
      try {
        await tryModify("add", {
          Product_Name: {
            id: crm_product_id,
            name: product_payload.Product_Name,
            Product_Code: product_payload.Product_Code,
          },
          ...quoteProductData,
        });
      } catch (addErr) {
        console.error("Failed to re-add product to quote:", addErr);
      }
    } else {
      console.error("Failed to update quote product:", error);
    }
  }
}
